/**
 * cameraLens.ts — the ONE conversion the web needs to show a shot's REAL lens: a full-frame focal
 * length (mm) from the camera's persisted horizontal aperture angle (degrees).
 *
 * ─── WHY THIS EXISTS (20260922, owner: "all the Lens are set to 10") ────────────────────────────
 *
 * `cameraobjects.focallength` IS A DEAD COLUMN. It has been `REAL NOT NULL DEFAULT(10)` since file
 * version 0001 and the camera record layer NEVER writes it: DocumentCameraObjectRecord.cs's UPDATE
 * (~line 598) and both INSERTs (~741, ~824) carry `apertureangle` only. The runtime FOV and the mm
 * the 3D editor shows are BOTH derived from `apertureangle`
 * (CameraUtilities.GetCameraFocalLengthForCameraApertureExactInverse). Only three writers ever set
 * `focallength` — coverage Apply / rough block (DatabaseManager.UpdateShotCameraFocalLength, which
 * bakes BOTH columns) and shot templates — so for every camera a user framed by hand it is still
 * the default 10. Reading it is what made every hand-framed shot report a 10 mm lens in the viewer
 * badge, the InfoBar, exports, the AI prompt's lens description and the coverage overview.
 *
 * ─── THE BAKE CONTRACT (this function is its exact inverse) ─────────────────────────────────────
 *
 * CameraUtilities.GetCameraApertureForCameraFullFrameFocalLength(f) is
 *   aperture° = Rad2Deg * atan(W / (2f))
 * with W = the EFFECTIVE width of the 35 mm full-frame sensor (36 x 24) cropped to
 * DocumentAspectRatio.DefaultAspectRatioType = 16:9. 16:9 is WIDER than the sensor's native 3:2, so
 * the crop is width-limited and W = 36 mm exactly. Inverting:
 *   f = (W / 2) / tan(aperture°) = 18 / tan(aperture°)
 * `FULL_FRAME_HALF_WIDTH_MM` below is that W / 2. (The same inverse lives in
 * `src/three/camera/lens.ts` as `fullFrameFocalLengthFromApertureAngle`, parameterized over sensor
 * and squeeze; this module is the dependency-free, validated, whole-mm form the loader wants — the
 * loader must not pull in the kernel-snapshot lens module for one number.)
 *
 * CAVEAT, PRE-EXISTING AND DELIBERATELY OUT OF SCOPE: the value is therefore a FULL-FRAME-EQUIVALENT
 * mm regardless of the project's own sensor size, because the kernel thinks in full-frame mm — the
 * coverage payload's `focalLength` is fed straight back through
 * GetCameraApertureForCameraFullFrameFocalLength on Apply. On a project whose sensor is not full
 * frame the 3D editor's own readout will differ from this number. That is how the wire has always
 * worked; showing the value that ROUND-TRIPS through Apply is the point.
 */

/** Half the effective full-frame sensor width at the default 16:9 — 36 mm / 2. See the bake contract. */
const FULL_FRAME_HALF_WIDTH_MM = 18;

const DEG2RAD = Math.PI / 180;

/**
 * The full-frame mm Unity stamps into EVERY shot camera it creates — the mirror of
 * `EditorObjectConstants.CameraDefaultFullFrameFocalLengthForNewScene = 35.0f`, baked into
 * `apertureangle` by DatabaseManager's shot-camera inserts (DatabaseManager.cs:1615 / :2503 / :27299)
 * and by ShotTemplateService. Keep the two in step.
 *
 * WHY IT IS ALSO A SENTINEL (20260922): because the bake is the only lens a never-framed shot ever
 * has, a persisted 35 is indistinguishable from "this camera was never framed" — there is no
 * "user chose a lens" bit anywhere in the schema. Anything that must tell a DELIBERATE lens from an
 * untouched one has to read this value as "no choice made"; see integration/roughBlock.ts's
 * requestedFocalFor, the one place that does.
 */
export const DEFAULT_NEW_SHOT_FOCAL_LENGTH_MM = 35;

/**
 * Full-frame focal length in WHOLE mm for a persisted `cameraobjects.apertureangle`, or `undefined`
 * when the angle cannot describe a lens (absent column, no enabled camera, the column's own 0
 * default, or anything non-finite / outside the open interval 0°–90°).
 *
 * Rounded to whole mm on purpose: Unity's own lens readout rounds (`roundValue = true` on
 * CameraUtilities.GetCameraFocalLengthForCameraAperture*) and the coverage overview's Lens cell is a
 * whole-mm input, so an unrounded 49.99999 would render as a lens nobody owns.
 */
export function fullFrameFocalLengthFromAperture(apertureDeg: number | null | undefined): number | undefined {
  if (typeof apertureDeg !== 'number' || !Number.isFinite(apertureDeg)) return undefined;
  // Open interval: 0 is the column default (and tan(0) = 0 would divide to Infinity); 90+ is not a
  // half-HFOV a real camera can hold, and tan(90°) does not evaluate to Infinity in floating point,
  // so it has to be excluded by hand rather than left to the arithmetic.
  if (apertureDeg <= 0 || apertureDeg >= 90) return undefined;
  return Math.round(FULL_FRAME_HALF_WIDTH_MM / Math.tan(apertureDeg * DEG2RAD));
}
